/**
 * Document Requirements Service
 * Handles API calls for document requirements and assignments
 */

import apiService from './api';

class DocumentRequirementsService {
  /**
   * Get current user's document requirements summary
   * Uses context-based authentication - user ID from JWT, tenant from Host header
   */
  async getMySummary() {
    try {
      const response = await apiService.request('/api/document-requirements/my-summary');
      return response;
    } catch (error) {
      console.error('Error fetching my requirements summary:', error);
      throw error;
    }
  }

  /**
   * Get current user's document assignments
   * Uses context-based authentication - user ID from JWT, tenant from Host header
   */
  async getMyAssignments(statusFilter = null) {
    try {
      let endpoint = '/api/document-requirements/my-assignments';
      if (statusFilter && statusFilter.length > 0) {
        const params = new URLSearchParams();
        statusFilter.forEach(status => params.append('status_filter', status));
        endpoint += `?${params.toString()}`;
      }
      const response = await apiService.request(endpoint);
      return response;
    } catch (error) {
      console.error('Error fetching my assignments:', error);
      throw error;
    }
  }

  /**
   * Check if current user can proceed through a process gate
   * Uses context-based authentication - user ID from JWT
   */
  async checkProcessGate(processType, processId) {
    try {
      const params = new URLSearchParams({
        process_type: processType,
        process_id: processId
      });
      const response = await apiService.request(`/api/document-requirements/process-gate/check?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Error checking process gate:', error);
      throw error;
    }
  }

  /**
   * Handle program registration document requirements for current user
   * Uses context-based authentication - user ID from JWT
   */
  async handleProgramRegistration(programId) {
    try {
      const response = await apiService.request('/api/document-requirements/integrate/program-registration', {
        method: 'POST',
        body: JSON.stringify({
          program_id: programId
        })
      });
      return response;
    } catch (error) {
      console.error('Error handling program registration:', error);
      throw error;
    }
  }

  /**
   * Handle event registration document requirements for current user
   * Uses context-based authentication - user ID from JWT
   */
  async handleEventRegistration(eventId) {
    try {
      const response = await apiService.request('/api/document-requirements/integrate/event-registration', {
        method: 'POST',
        body: JSON.stringify({
          event_id: eventId
        })
      });
      return response;
    } catch (error) {
      console.error('Error handling event registration:', error);
      throw error;
    }
  }

  /**
   * Get all requirements for admin
   */
  async getClubRequirements(activeOnly = true) {
    try {
      const params = new URLSearchParams({ active_only: activeOnly });
      const response = await apiService.request(`/api/document-requirements/requirements?${params.toString()}`);
      return response;
    } catch (error) {
      console.error('Error fetching club requirements:', error);
      throw error;
    }
  }

  /**
   * Create a new document requirement (admin only)
   */
  async createRequirement(requirementData) {
    try {
      const response = await apiService.request('/api/document-requirements/requirements', {
        method: 'POST',
        body: JSON.stringify(requirementData)
      });
      return response;
    } catch (error) {
      console.error('Error creating requirement:', error);
      throw error;
    }
  }

  /**
   * Update an existing document requirement (admin only)
   */
  async updateRequirement(requirement_id, updates) {
    try {
      const response = await apiService.request(`/api/document-requirements/requirements/${requirement_id}`, {
        method: 'PUT',
        body: JSON.stringify(updates)
      });
      return response;
    } catch (error) {
      console.error('Error updating requirement:', error);
      throw error;
    }
  }

  /**
   * Delete a document requirement (admin only)
   */
  async deleteRequirement(requirement_id) {
    try {
      const response = await apiService.request(`/api/document-requirements/requirements/${requirement_id}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error('Error deleting requirement:', error);
      throw error;
    }
  }

  /**
   * Bulk assign requirement to users (admin only)
   */
  async bulkAssignRequirement(bulkAssignmentData) {
    try {
      const response = await apiService.request('/api/document-requirements/requirements/bulk-assign', {
        method: 'POST',
        body: JSON.stringify(bulkAssignmentData)
      });
      return response;
    } catch (error) {
      console.error('Error bulk assigning requirement:', error);
      throw error;
    }
  }

  /**
   * Waive an assignment (admin only)
   */
  async waiveAssignment(assignmentId, waiveReason) {
    try {
      const response = await apiService.request(`/api/document-requirements/assignments/${assignmentId}/waive`, {
        method: 'PUT',
        body: JSON.stringify({
          waive_reason: waiveReason
        })
      });
      return response;
    } catch (error) {
      console.error('Error waiving assignment:', error);
      throw error;
    }
  }

  /**
   * Get overdue assignments (admin only)
   */
  async getOverdueAssignments() {
    try {
      const response = await apiService.request('/api/document-requirements/analytics/overdue');
      return response;
    } catch (error) {
      console.error('Error fetching overdue assignments:', error);
      throw error;
    }
  }

  /**
   * Get club-wide compliance statistics (admin only)
   */
  async getComplianceStats() {
    try {
      const response = await apiService.request('/api/document-requirements/analytics/compliance');
      return response;
    } catch (error) {
      console.error('Error fetching compliance stats:', error);
      throw error;
    }
  }

  /**
   * Complete a document requirement assignment
   */
  async completeAssignment(assignmentId, completionData) {
    try {
      console.log('[DocumentRequirementsService] Completing assignment:', assignmentId);
      console.log('[DocumentRequirementsService] Request URL:', `/api/document-requirements/assignments/${assignmentId}/complete`);
      console.log('[DocumentRequirementsService] Completion data:', completionData);

      const response = await apiService.request(`/api/document-requirements/assignments/${assignmentId}/complete`, {
        method: 'POST',
        body: JSON.stringify(completionData)
      });

      console.log('[DocumentRequirementsService] Response:', response);
      return response;
    } catch (error) {
      console.error('[DocumentRequirementsService] Error completing assignment:', error);
      console.error('[DocumentRequirementsService] Error details:', {
        message: error.message,
        status: error.status,
        assignmentId
      });
      throw error;
    }
  }

  /**
   * Get document requirement template by ID
   */
  async getRequirement(requirement_id) {
    try {
      const response = await apiService.request(`/api/document-requirements/requirements/${requirement_id}`);
      return response;
    } catch (error) {
      console.error('Error fetching requirement:', error);
      throw error;
    }
  }
}

const documentRequirementsService = new DocumentRequirementsService();
export default documentRequirementsService;